import z from "zod";
import { ChronoUnit, DayOfWeek, LocalDate } from "@js-joda/core";

const zLocalDate = z.custom<LocalDate>((value) => value instanceof LocalDate, {
  message: "Expected js-joda LocalDate",
});

// ---------------------------
// 1. Core primitives
// ---------------------------

export const every_x_days = "every-x-days";
export const every_x_weeks = "every-x-weeks";
export const every_x_months = "every-x-months";
export const every_x_months_last_working_day =
  "every-x-months-last-working-day";
export const every_x_months_last_day = "every-x-months-last-day";
export const every_x_years = "every-x-years";
export const weekdays = "weekdays";
export const specific_dates = "specific-dates";
export const business_days = "business-days";

export const kindParser = z.union([
  z.literal(every_x_days),
  z.literal(every_x_weeks),
  z.literal(every_x_months),
  z.literal(every_x_months_last_working_day),
  z.literal(every_x_months_last_day),
  z.literal(every_x_years),
  z.literal(weekdays),
  z.literal(specific_dates),
  z.literal(business_days),
]);

export type RecurrenceKinds = z.infer<typeof kindParser>;

// export type  =
//   | typeof every_x_days
//   | typeof every_x_weeks
//   | typeof every_x_months
//   | typeof every_x_months_last_working_day
//   | typeof every_x_years
//   | typeof weekdays
//   | typeof specific_dates
//   | typeof every_x_months_last_day
//   | typeof business_days;

// LocalDate validator
export const LocalDateSchema = z.custom<LocalDate>(
  (v) => v instanceof LocalDate,
  { message: "Expected js-joda LocalDate" },
);

// Weekday enum (js-joda DayOfWeek)
export const WeekdaySchema = z.custom<DayOfWeek>(
  (v) => v instanceof DayOfWeek,
  { message: "Expected DayOfWeek" },
);

// Every X days (anchor by weekday)
const EveryXDays = z.object({
  kind: z.literal(every_x_days),
  interval: z.number().int().positive(),
  startsOnWeekday: WeekdaySchema,
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Every X weeks (anchor by date)
const EveryXWeeks = z.object({
  kind: z.literal(every_x_weeks),
  interval: z.number().int().positive(),
  weekday: WeekdaySchema,
});

// Every X months (day-of-month anchor)
const EveryXMonths = z.object({
  kind: z.literal(every_x_months),
  align: z.literal("fixed-day"),
  dayOfMonth: z.number().int().min(1).max(31),
  interval: z.number().int().positive(),
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Every X months (day-of-month anchor)
const EveryXMonths_Last_Working_Day = z.object({
  kind: z.literal(every_x_months_last_working_day),
  align: z.literal("last-working-day"),
  interval: z.number().int().positive(),
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Every X months (day-of-month anchor)
const EveryXMonths_Last_Day = z.object({
  kind: z.literal(every_x_months_last_day),
  align: z.literal("last-day"),
  interval: z.number().int().positive(),
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Every X years (month + day anchor)
const EveryXYears = z.object({
  kind: z.literal(every_x_years),
  interval: z.number().int().positive(),
  month: z.number().int().min(1).max(12),
  dayOfMonth: z.number().int().min(1).max(31),
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Specific weekdays (e.g., Mon/Wed/Fri)
const Weekdays = z.object({
  kind: z.literal(weekdays),
  weekdays: z.array(WeekdaySchema).nonempty(),
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Business days only (skip weekends)
const BusinessDays = z.object({
  kind: z.literal(business_days),
  interval: z.number().int().positive(), // this is the same as above? would need to limit 0 to 4?
  // Recurrence anchor date (cadence origin). Not a filter window.
  recurrenceAnchorDate: LocalDateSchema,
});

// Specific irregular dates
const SpecificDates = z.object({
  kind: z.literal(specific_dates),
  dates: z.array(LocalDateSchema).nonempty(),
});

export const RecurrenceRuleSchema = z.discriminatedUnion("kind", [
  EveryXDays,
  EveryXWeeks,
  EveryXMonths,
  EveryXMonths_Last_Working_Day,
  EveryXMonths_Last_Day,
  EveryXYears,
  Weekdays,
  BusinessDays,
  SpecificDates,
]);

export type Schedule = z.infer<typeof RecurrenceRuleSchema>;

const monthlyRules = z.union([
  EveryXMonths,
  EveryXMonths_Last_Working_Day,
  EveryXMonths_Last_Day,
]);

export type MonthlyRecurrenceRule = z.infer<typeof monthlyRules>;

// TypeScript type inference
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;

function staticEnum<T>(values: readonly T[]) {
  return z.custom<T>((v) => values.includes(v as T));
}

export const ChronoUnitSchema = staticEnum<ChronoUnit>([
  ChronoUnit.DAYS,
  ChronoUnit.WEEKS,
  ChronoUnit.MONTHS,
  ChronoUnit.YEARS,
] as const);

// Optional inclusion window for when the item is active (filters occurrences).
// This is separate from recurrence anchors (recurrenceAnchorDate) which define cadence.
const dateFilters = z
  .object({
    dateFromInclusive: zLocalDate.optional(),
    dateToInclusive: zLocalDate.optional(),
  })
  .optional();

const sequentialRule = z.discriminatedUnion("amountType", [
  z.object({
    amountType: z.literal("percentage"),
    percentage: z.number(), // fraction 0–1
    chronoUnits: ChronoUnitSchema,
    amountOfChronoUnits: z.number(),
  }),
  z.object({
    amountType: z.literal("smp"),
    chronoUnits: ChronoUnitSchema,
    amountOfChronoUnits: z.number(),
  }),
]);

const parentalLeavePolicy = z.object({
  startsFrom: zLocalDate,
  afterPolicy: z.enum(["full-pay", "leave-job", "last-rate"]).default("full-pay"),
  sequentialRules: z.array(sequentialRule),
});

// Redundancy for a salary: the salary stops after `endDate`, and on that date a
// redundancy payment worth `weeks` of the salary is paid (tax-free up to the
// statutory £30k limit, taxable above it), plus an optional taxable payment in
// lieu of notice (PILON) worth `noticeMonths` of the monthly salary.
const redundancyPolicy = z.object({
  endDate: zLocalDate,
  // Date the redundancy settlement is actually paid (often the final payroll
  // run, not the last day of employment). Defaults to endDate when absent.
  paidOn: zLocalDate.optional(),
  weeks: z.number().nonnegative(),
  // "full" = weeks paid at actual weekly salary; "statutory" = capped at the
  // government's statutory weekly pay limit.
  payBasis: z.enum(["full", "statutory"]).default("full"),
  noticeMonths: z.number().int().nonnegative().optional(),
  // Outstanding accrued holiday, paid as normal (fully taxable) pay.
  holidayDays: z.number().nonnegative().optional(),
});

// A bonus: a taxable one-off payment, either a percentage of the salary or a
// fixed amount, on a given date. `kind` defaults to percentage for backward
// compatibility with older records that only stored a percentage.
const bonus = z.object({
  date: zLocalDate,
  kind: z.enum(["percentage", "fixed"]).default("percentage"),
  percentage: z.number().optional(), // fraction 0–1, when kind = percentage
  amountPence: z.number().int().optional(), // when kind = fixed
});

export const incomesItem = z.object({
  id: z.string().uuid(),
  amountPence: z.number().int(),
  userId: z.string().uuid(),
  taxable: z.boolean(),
  recurrence: RecurrenceRuleSchema,
  dateFilters,
  description: z.string(),
  type: z.literal("INCOME_ITEM"),
  parentalLeavePolicy: parentalLeavePolicy.optional(),
  redundancy: redundancyPolicy.optional(),
  bonuses: z.array(bonus).optional(),
});

export const outgoingsItem = z.object({
  id: z.string().uuid(),
  amountPence: z.number().int(),
  description: z.string(),
  type: z.literal("OUTGOINGS_ITEM"),
  recurrence: RecurrenceRuleSchema,
  dateFilters,
});

export const cashFlowItem = z.discriminatedUnion("type", [
  incomesItem,
  outgoingsItem,
]);

export const cashFlowItems = z.array(cashFlowItem);

export type ParentalLeavePolicy = z.infer<typeof parentalLeavePolicy>;
export type RedundancyPolicy = z.infer<typeof redundancyPolicy>;
export type Bonus = z.infer<typeof bonus>;
export type IncomesItem = z.infer<typeof incomesItem>;
export type IncomesItems = Array<IncomesItem>;

export type OutgoingItem = z.infer<typeof outgoingsItem>;
export type OutgoingItems = Array<OutgoingItem>;

export type CashflowItem = z.infer<typeof cashFlowItem>;
export type CashflowItems = z.infer<typeof cashFlowItems>;

export interface RecurrenceEngine {
  firstOnOrAfter(from: LocalDate): LocalDate | null;
  next(after: LocalDate): LocalDate | null;
}

export type PayBreakdownSegment = {
  days: number;
  label: string; // e.g. "full pay", "50%", "SMP"
};

export type LedgerEntry_Gross = {
  date: LocalDate;
  itemId: string;
  userId: string;
  name: string;
  taxDates: {
    start: LocalDate;
    end: LocalDate;
  };
  amountPence: number; // +income / -outgoing (integer pence) -> should only be positive!
  payBreakdown?: PayBreakdownSegment[];
};

export type LedgerEntry_Net = {
  date: LocalDate;
  itemId: string;
  name: string;
  taxDates: {
    start: LocalDate;
    end: LocalDate;
  };
  amountPence: number; // +income / -outgoing (integer pence)
  percentageAlreadyTaxed: number;
  payBreakdown?: PayBreakdownSegment[];
};

export type PaySegment = {
  from: LocalDate;
  to: LocalDate;
} & ({ multiplier: number } | { smp: true });

export type PayPeriodRule =
  | { kind: "calendar-month" } // 1st → last day
  | { kind: "fixed-length"; days: number } // e.g. 28-day cycles
  | { kind: "to-next-pay-date" }; // common for contractors

export type UserId = string;

export interface User {
  id: UserId;

  // tax identity
  country: "rUK" | "scotland";

  taxCode?: string; // e.g. "1257L"
  niCategory?: "A" | "B" | "C";

  hasStudentLoan?: boolean;
  studentLoanPlan?: 1 | 2 | 4 | "postgrad";
}

/* DB */

/**
 * ---------------------------
 * Shared DB schemas
 * ---------------------------
 */

const dbEveryXDays = z.object({
  kind: z.literal(every_x_days),
  interval: z.number().int().positive(),
  startsOnWeekday: WeekdaySchema,
  recurrenceAnchorDate: z.string(),
});

const dbEveryXWeeks = z.object({
  kind: z.literal(every_x_weeks),
  interval: z.number().int().positive(),
  weekday: z.string().optional(),
  recurrenceAnchorDate: z.string().optional(),
});

const dbEveryXMonths = z.object({
  kind: z.literal(every_x_months),
  align: z.literal("fixed-day"),
  dayOfMonth: z.number().int().min(1).max(31),
  interval: z.number().int().positive(),
  recurrenceAnchorDate: z.string(),
});

const dbEveryXMonthsLastWorkingDay = z.object({
  kind: z.literal(every_x_months_last_working_day),
  align: z.literal("last-working-day"),
  interval: z.number().int().positive(),
  recurrenceAnchorDate: z.string(),
});

const dbEveryXMonthsLastDay = z.object({
  kind: z.literal(every_x_months_last_day),
  align: z.literal("last-day"),
  interval: z.number().int().positive(),
  recurrenceAnchorDate: z.string(),
});

const dbEveryXYears = z.object({
  kind: z.literal(every_x_years),
  interval: z.number().int().positive(),
  month: z.number().int().min(1).max(12),
  dayOfMonth: z.number().int().min(1).max(31),
  recurrenceAnchorDate: z.string(),
});

const dbWeekdays = z.object({
  kind: z.literal(weekdays),
  weekdays: z.array(WeekdaySchema).nonempty(),
  recurrenceAnchorDate: z.string(),
});

const dbBusinessDays = z.object({
  kind: z.literal(business_days),
  interval: z.number().int().positive(),
  recurrenceAnchorDate: z.string(),
});

const dbSpecificDates = z.object({
  kind: z.literal(specific_dates),
  dates: z.array(z.string()).nonempty(),
});

export const dbRecurrenceSchema = z.discriminatedUnion("kind", [
  dbEveryXDays,
  dbEveryXWeeks,
  dbEveryXMonths,
  dbEveryXMonthsLastWorkingDay,
  dbEveryXMonthsLastDay,
  dbEveryXYears,
  dbWeekdays,
  dbBusinessDays,
  dbSpecificDates,
]);

export const dbBonusSchema = z.object({
  date: z.string(), // Firestore Timestamp / string / etc
  kind: z.enum(["percentage", "fixed"]).nullish(),
  percentage: z.number().nullish(),
  amountPence: z.number().nullish(),
});

export const dbParentalLeavePolicySchema = z.object({
  startsFrom: z.string(),
  afterPolicy: z.enum(["full-pay", "leave-job", "last-rate"]).optional(),
  sequentialRules: z.array(
    z.object({
      amountType: z.enum(["percentage", "smp"]).optional(),
      percentage: z.number().optional(),
      chronoUnits: z.string(),
      amountOfChronoUnits: z.number(),
    }),
  ),
});

export const dbBaseCashflowItemSchema = z.object({
  id: z.string(),
  type: z.enum(["INCOME_ITEM", "OUTGOINGS_ITEM"]),
  recurrence: dbRecurrenceSchema,
  dateFilters: z.object({
    dateFromInclusive: z.string().nullable(),
    dateToInclusive: z.string().nullable(),
  }),
  amountPence: z.number(),
  description: z.string(),
});

/**
 * ---------------------------
 * INCOME_ITEM
 * ---------------------------
 */

export const dbRedundancySchema = z.object({
  endDate: z.string(),
  paidOn: z.string().nullish(),
  weeks: z.number(),
  payBasis: z.enum(["full", "statutory"]).nullish(),
  noticeMonths: z.number().nullish(),
  holidayDays: z.number().nullish(),
});

export const dbIncomeItemSchema = dbBaseCashflowItemSchema.extend({
  type: z.literal("INCOME_ITEM"),
  parentalLeavePolicy: dbParentalLeavePolicySchema.nullish(),
  redundancy: dbRedundancySchema.nullish(),
  bonuses: z.array(dbBonusSchema).optional().default([]),
  userId: z.string().nullable(),
  taxable: z.boolean(),
});

/**
 * ---------------------------
 * OUTGOINGS_ITEM
 * ---------------------------
 */

export const dbOutgoingsItemSchema = dbBaseCashflowItemSchema.extend({
  type: z.literal("OUTGOINGS_ITEM"),
});

/**
 * ---------------------------
 * Discriminated union
 * ---------------------------
 */

export const dbCashflowItemSchema = z.discriminatedUnion("type", [
  dbIncomeItemSchema,
  dbOutgoingsItemSchema,
]);

export type DbBonus = z.infer<typeof dbBonusSchema>;
export type DbIncomeItem = z.infer<typeof dbIncomeItemSchema>;
export type DbOutgoingsItem = z.infer<typeof dbOutgoingsItemSchema>;
export type DbCashflowItem = z.infer<typeof dbCashflowItemSchema>;
